import React, { useState, useEffect, useRef, useCallback } from 'react';
import { verifyCode, resendVerificationCode } from '../services/auth.service';

const EmailVerification = ({ email, onSuccess, onBack }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [manualCode, setManualCode] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSubmittedCode, setLastSubmittedCode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [codeSentAt, setCodeSentAt] = useState(() => Date.now());
  const inputRefs = useRef([]);
  const loadingRef = useRef(false);
  const manualInputRef = useRef(null);

  useEffect(() => {
    // Countdown timer - recalculé depuis codeSentAt
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - codeSentAt) / 1000);
      const remaining = Math.max(0, 15 * 60 - elapsed);
      setTimeLeft(remaining);
      setCanResend(remaining === 0);
    }, 1000);

    return () => clearInterval(timer);
  }, [codeSentAt]);

  // Synchroniser loadingRef avec loading state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // handleSubmit défini en premier pour éviter la référence avant initialisation
  const handleSubmit = useCallback(async (codeValue) => {
    if (loadingRef.current) return; // Guard against double submission
    setLoading(true);
    setError(null);

    try {
      const data = await verifyCode(email, codeValue);
      localStorage.setItem('token', data.token);
      localStorage.setItem('artist', JSON.stringify(data.artist));
      onSuccess(data.artist);
    } catch (err) {
      setError(err.message);
      // Vider les inputs pour forcer la re-saisie
      setCode(['', '', '', '', '', '']);
      setManualCode('');
      setLastSubmittedCode(null);
      if (!useManualInput) {
        inputRefs.current[0]?.focus();
      } else {
        manualInputRef.current?.focus();
      }
    } finally {
      setLoading(false);
    }
  }, [email, onSuccess, useManualInput]);

  // Soumission manuelle depuis le bouton Valider
  const handleManualSubmit = useCallback(() => {
    const codeValue = useManualInput ? manualCode.replace(/\D/g, '') : code.join('');
    if (codeValue.length === 6) {
      setLastSubmittedCode(codeValue);
      handleSubmit(codeValue);
    }
  }, [manualCode, code, useManualInput, handleSubmit]);

  // Auto-submit quand les 6 chiffres sont saisis (manuellement ou par collage)
  useEffect(() => {
    const codeString = code.join('');
    const isComplete = codeString.length === 6 && code.every(digit => digit !== '');

    // Éviter la resoumission du même code après une erreur
    if (isComplete && !loading && codeString !== lastSubmittedCode) {
      setLastSubmittedCode(codeString);
      // Petit délai pour s'assurer que l'UI ait mis à jour les champs
      const timeoutId = setTimeout(() => {
        handleSubmit(codeString);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [code, loading, handleSubmit, lastSubmittedCode]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Note: auto-submit géré par useEffect pour inclure le collage
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;

    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedData[i] || '';
    }
    setCode(newCode);

    // Si 6 chiffres collés, focus sur le dernier champ et la soumission auto se déclenchera
    if (pastedData.length === 6) {
      inputRefs.current[5]?.focus();
      inputRefs.current[5]?.select();
    } else {
      // Focus le prochain champ vide
      const nextEmptyIndex = newCode.findIndex((digit) => digit === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      }
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError(null);

    try {
      await resendVerificationCode(email);
      setCodeSentAt(Date.now()); // Redémarre le timer
      setTimeLeft(15 * 60);
      setCanResend(false);
      setCode(['', '', '', '', '', '']);
      setManualCode('');
      setLastSubmittedCode(null);
      if (!useManualInput) {
        inputRefs.current[0]?.focus();
      } else {
        manualInputRef.current?.focus();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Bascule entre les modes de saisie
  const toggleInputMode = () => {
    setUseManualInput(!useManualInput);
    setError(null);
    if (!useManualInput) {
      // Passe au mode manuel : pré-remplir avec les chiffres déjà saisis
      setManualCode(code.join(''));
      setTimeout(() => manualInputRef.current?.focus(), 100);
    } else {
      // Passe au mode champs individuels : répartir le code
      const digits = manualCode.split('').slice(0, 6);
      const newCode = ['', '', '', '', '', ''];
      digits.forEach((d, i) => { if (i < 6) newCode[i] = d; });
      setCode(newCode);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ 
          width: '60px', 
          height: '60px', 
          borderRadius: '50%', 
          background: 'linear-gradient(135deg, #7A0C0C 0%, #a32f2f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1rem'
        }}>
          <i className="fas fa-envelope" style={{ color: 'white', fontSize: '24px' }} />
        </div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111', marginBottom: '0.5rem' }}>
          Vérifiez votre email
        </h2>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          Un code de vérification a été envoyé à
        </p>
        <p style={{ color: '#7A0C0C', fontWeight: 600, fontSize: '0.95rem' }}>
          {email}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ 
          background: '#fee2e2', 
          border: '1px solid #fecaca', 
          borderRadius: '8px', 
          padding: '12px', 
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className="fas fa-circle-exclamation" style={{ color: '#dc2626' }} />
          <span style={{ color: '#991b1b', fontSize: '0.9rem' }}>{error}</span>
        </div>
      )}

      {/* Code Input - Mode champs individuels */}
      {!useManualInput && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '1rem' }}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={loading}
              style={{
                width: '50px',
                height: '60px',
                fontSize: '24px',
                fontWeight: 'bold',
                textAlign: 'center',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                background: 'white',
                color: '#111',
                transition: 'all 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#7A0C0C';
                e.target.style.boxShadow = '0 0 0 3px rgba(122, 12, 12, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
          ))}
        </div>
      )}

      {/* Code Input - Mode champ unique (alternative accessible) */}
      {useManualInput && (
        <div style={{ marginBottom: '1rem' }}>
          <input
            ref={manualInputRef}
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="Entrez le code à 6 chiffres"
            value={manualCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setManualCode(value);
            }}
            disabled={loading}
            style={{
              width: '100%',
              height: '50px',
              fontSize: '18px',
              textAlign: 'center',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              background: 'white',
              color: '#111',
              transition: 'all 0.2s',
              outline: 'none',
              padding: '0 16px',
              letterSpacing: '8px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#7A0C0C';
              e.target.style.boxShadow = '0 0 0 3px rgba(122, 12, 12, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualCode.length === 6) {
                handleManualSubmit();
              }
            }}
          />
        </div>
      )}

      {/* Bouton Valider */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={handleManualSubmit}
          disabled={loading || (useManualInput ? manualCode.length !== 6 : code.join('').length !== 6)}
          style={{
            width: '100%',
            height: '48px',
            background: loading 
              ? '#d1d5db' 
              : (useManualInput ? manualCode.length === 6 : code.join('').length === 6) 
                ? '#7A0C0C' 
                : '#d1d5db',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: (loading || (useManualInput ? manualCode.length !== 6 : code.join('').length !== 6)) 
              ? 'not-allowed' 
              : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin" /> Vérification en cours...
            </>
          ) : (
            <>
              <i className="fas fa-check-circle" /> Valider
            </>
          )}
        </button>
      </div>

      {/* Toggle Input Mode */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <button
          onClick={toggleInputMode}
          disabled={loading}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
            padding: '8px 16px',
            textDecoration: 'underline'
          }}
        >
          {useManualInput ? (
            <><i className="fas fa-th" /> Utiliser les cases séparées</>
          ) : (
            <><i className="fas fa-keyboard" /> Saisir dans un champ unique</>
          )}
        </button>
      </div>

      {/* Timer */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {timeLeft > 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            Code expire dans <span style={{ color: '#7A0C0C', fontWeight: 600 }}>{formatTime(timeLeft)}</span>
          </p>
        ) : (
          <p style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: 600 }}>
            Code expiré
          </p>
        )}
      </div>

      {/* Resend Button */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {canResend || timeLeft === 0 ? (
          <button
            onClick={handleResend}
            disabled={resendLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#7A0C0C',
              fontWeight: 600,
              cursor: resendLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              padding: '8px 16px',
              borderRadius: '6px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!resendLoading) e.target.style.background = 'rgba(122, 12, 12, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
            }}
          >
            {resendLoading ? (
              <>
                <i className="fas fa-circle-notch fa-spin" /> Envoi en cours...
              </>
            ) : (
              <>
                <i className="fas fa-redo" /> Renvoyer le code
              </>
            )}
          </button>
        ) : (
          <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
            Renvoyer disponible dans {formatTime(timeLeft)}
          </p>
        )}
      </div>

      {/* Back Button */}
      <div style={{ textAlign: 'center' }}>
        <button
          onClick={onBack}
          disabled={loading}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
            padding: '8px 16px'
          }}
        >
          <i className="fas fa-arrow-left" /> Retour
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;
